import { isEmpty } from 'lodash-es';

import has from '@dojo/framework/core/has';
// make generated .d.ts file valid
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { DNode, WNodeFactory, WidgetProperties } from '@dojo/framework/core/interfaces';
import { createICacheMiddleware } from '@dojo/framework/core/middleware/icache';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { ThemeProperties } from '@dojo/framework/core/middleware/theme';
import { create, destroy, w } from '@dojo/framework/core/vdom';
import { LandingPage } from '@holmescorp/landing-page';
import { lccstate, locationChangeProcess, recommended, userstate, whichActiveViewProcess } from '@holmescorp/lcc-state';
import { subscribe } from '@holmescorp/pub-sub';

import { CurrentView } from '../interfaces';
import { AssessView } from './assess-view';
import { AssessmentChooser } from './assessment-chooser';
import { CarouselView } from './carousel-view';
import { ExploreView } from './explore-view';
import { PracticeView } from './practice-view';
import { WhereYouLeftOff } from './where-you-left-off';

interface ActiveViewIcache {
	view: DNode;
}

const icache = createICacheMiddleware<ActiveViewIcache>();
const factory = create({ icache, recommended, lccstate, userstate, destroy });

export const ActiveView = factory(({ middleware: { icache, recommended, lccstate, userstate, destroy } }) => {
	const useAssessView = !has('no-explore-assess');
	const useStudyView = !has('no-explore-study');
	const usePassView = !has('no-explore-pass');
	const useLandingView = has('use-landing-page');
	const useWYLO = has('use-wylo');
	const firstViewtype: CurrentView = useAssessView ? 'assess' : useStudyView ? 'study' : 'practice';
	const { get: uget, path: upath } = userstate;

	const trackingwylo = uget(upath('toolstate', 'trackingwylo'));
	let alwaysResume: (() => void) | null = null;
	let alwaysResumePromise: Promise<void> | null = null;
	const waitForAlwaysResume = async (): Promise<void> => {
		if (alwaysResume) {
			return;
		}
		// If externalPlayis not yet set, await the Promise
		if (!alwaysResumePromise) {
			alwaysResumePromise = new Promise((resolve) => {
				const checkInterval = setInterval(() => {
					if (alwaysResume) {
						clearInterval(checkInterval);
						resolve();
					}
				}, 100); // Check every 100ms
			});
		}
		return alwaysResumePromise;
	};
	const wyloWidget = w(WhereYouLeftOff, {
		onAlwaysResume: (resumeFunction: () => void) => {
			alwaysResume = resumeFunction;
		}
	});

	const { complete: landingCompleted = false } = uget(upath('toolstate', 'landing'));
	const useLanding = useLandingView && !landingCompleted;

	const selectView = (viewtype: CurrentView): DNode => {
		let view: DNode;
		lccstate.run(whichActiveViewProcess)({ whichActive: viewtype });
		switch (viewtype) {
			case 'assess':
				if (useAssessView) {
					userstate.run(locationChangeProcess)({ location: '/explore-assess' });
					view = has('use-assessment-chooser')
						? w(AssessmentChooser, { switchView, setTitle, xqa: 'assessment-chooser' })
						: w(AssessView, { switchView });
				} else {
					return selectView(firstViewtype);
				}
				break;
			case 'practice':
				if (usePassView) {
					userstate.run(locationChangeProcess)({ location: '/explore-practice' });
					view = w(PracticeView, { switchView });
				} else {
					return selectView(firstViewtype);
				}
				break;
			case 'study':
			default:
				if (useStudyView) {
					userstate.run(locationChangeProcess)({ location: '/explore' });
					view = has('use-carousel-view') ? w(CarouselView, { switchView }) : w(ExploreView, { switchView });
				} else {
					return selectView(firstViewtype);
				}
				break;
		}
		return view;
	};

	const switchView = (viewtype: CurrentView) => {
		icache.set('view', selectView(viewtype));
	};

	const setTitle = (title: string) => {
		if (title) {
			document.title = title;
		}
	};

	const view = icache.getOrSet('view', () => {
		const h = subscribe('active-view', (topic, viewtype) => {
			switchView(viewtype);
		});
		destroy(() => {
			h.destroy();
		});

		const { get, path } = lccstate;
		const whichView: CurrentView | null = get(path('whichActiveView'), true);
		lccstate.run(whichActiveViewProcess)({ whichActive: null });
		if (useWYLO) {
			if (!isEmpty(trackingwylo.tracking)) {
				if (trackingwylo.always_resume) {
					(async () => {
						await waitForAlwaysResume();
						if (alwaysResume) {
							return alwaysResume();
						} else {
							console.warn('External play function is still loading.');
						}
					})();
				}
				return wyloWidget;
			}
		}
		if (useLanding) {
			userstate.run(locationChangeProcess)({ location: '/' });
			return w(LandingPage, {});
		} else if (whichView) {
			return selectView(whichView);
		} else if ((recommended.allcomplete() && usePassView) || (usePassView && !useStudyView)) {
			userstate.run(locationChangeProcess)({ location: '/explore-practice' });
			return w(PracticeView, { switchView });
		} else if ((recommended.assessmentsComplete() && useStudyView) || (useStudyView && !useAssessView)) {
			userstate.run(locationChangeProcess)({ location: '/explore' });
			return has('use-carousel-view') ? w(CarouselView, { switchView }) : w(ExploreView, { switchView });
		} else if (useAssessView) {
			userstate.run(locationChangeProcess)({ location: '/explore-assess' });
			return has('use-assessment-chooser')
				? w(AssessmentChooser, { switchView, setTitle, xqa: 'assessment-chooser' })
				: w(AssessView, { switchView });
		}
	});

	return view;
});
